import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Fab,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Pagination,
  Tabs,
  Tab,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Filter,
  List,
  LayoutGrid,
  Calendar,
  SortAsc,
  ChevronDown,
  X,
} from 'lucide-react';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppDispatch, useAppSelector } from '@/store';
import { openModal, selectTaskViewMode, setTaskViewMode } from '@/store/slices/uiSlice';
import { selectTaskFilters, updateTaskFilter, clearTaskFilters } from '@/store/slices/filtersSlice';
import { TaskStatus, TaskPriority } from '@/types';

import TaskListView from '../components/TaskListView';
import TaskKanbanView from '../components/TaskKanbanView';
import TaskDetailModal from '../components/TaskDetailModal';
import CreateTaskModal from '../components/CreateTaskModal';

/**
 * TasksPage
 * Main task management view with multiple view modes
 * Supports list, kanban, and calendar views
 * Mobile-first responsive design
 */
const TasksPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = useAppSelector(selectTaskViewMode);
  const filters = useAppSelector(selectTaskFilters);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  /** RACI tab: 0=Responsible, 1=Accountable, 2=Consulted, 3=Informed */
  const [raciTab, setRaciTab] = useState(0);
  const raciRoleMap: Record<number, 'responsible' | 'accountable' | 'consulted' | 'informed'> = {
    0: 'responsible',
    1: 'accountable',
    2: 'consulted',
    3: 'informed',
  };
  const selectedRaciRole = raciRoleMap[raciTab];

  // Check if task detail should be shown
  const selectedTaskId = searchParams.get('taskId');

  // Build server filter: status list, projectId, and raciRole for my-tasks API
  const serverFilters = useMemo(
    () => ({
      status: filters.status,
      projectId: filters.projectId,
      raciRole: selectedRaciRole,
    }),
    [filters.status, filters.projectId, selectedRaciRole]
  );

  // Fetch tasks with pagination and server-side status/projectId/raciRole filter
  const { data, isLoading, refetch } = useQuery({
    queryKey: [...QUERY_KEYS.myTasks, page, pageSize, serverFilters],
    queryFn: () =>
      taskService.getMyTasks(page, pageSize, {
        status: filters.status?.length ? filters.status : undefined,
        projectId: filters.projectId,
        raciRole: selectedRaciRole,
      }),
    staleTime: 30000,
    keepPreviousData: true, // Prevent flickering when switching tabs
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  const tasks = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Calculate incomplete tasks count (not completed)
  const incompleteCount = useMemo(() => {
    return tasks.filter(task => task.status !== TaskStatus.COMPLETED).length;
  }, [tasks]);

  // Client-side search and priority filter on current page
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (task) =>
          task.title.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query)
      );
    }

    if (filters.priority && filters.priority.length > 0) {
      result = result.filter((task) => filters.priority?.includes(task.priority));
    }

    return result;
  }, [tasks, searchQuery, filters.priority]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Reset to page 1 when filters that affect server request change
  React.useEffect(() => {
    setPage(1);
  }, [filters.status, filters.projectId, selectedRaciRole]);

  // Count active filters
  const activeFilterCount = [
    filters.status?.length || 0,
    filters.priority?.length || 0,
    filters.isOverdue ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'list' | 'kanban' | 'calendar' | null
  ) => {
    if (newMode) {
      dispatch(setTaskViewMode(newMode));
    }
  };

  const handleCreateTask = () => {
    dispatch(openModal({ type: 'taskCreate' }));
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status: TaskStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];
    dispatch(updateTaskFilter({ status: newStatuses.length > 0 ? newStatuses : undefined }));
  };

  const handlePriorityFilter = (priority: TaskPriority) => {
    const currentPriorities = filters.priority || [];
    const newPriorities = currentPriorities.includes(priority)
      ? currentPriorities.filter((p) => p !== priority)
      : [...currentPriorities, priority];
    dispatch(updateTaskFilter({ priority: newPriorities.length > 0 ? newPriorities : undefined }));
  };

  const handleClearFilters = () => {
    dispatch(clearTaskFilters());
    handleFilterClose();
  };

  return (
    <Box>
      {/* Page Header - Compact on mobile */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
          My Tasks
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {total > 0
            ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total} task${total !== 1 ? 's' : ''}`
            : 'No tasks'}
        </Typography>
      </Box>

      {/* RACI Role Tabs */}
      <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={raciTab}
          onChange={(_, v) => setRaciTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.85rem' },
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Responsible</span>
                {raciTab === 0 && incompleteCount > 0 && (
                  <Chip 
                    label={incompleteCount} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Accountable</span>
                {raciTab === 1 && incompleteCount > 0 && (
                  <Chip 
                    label={incompleteCount} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Consulted</span>
                {raciTab === 2 && incompleteCount > 0 && (
                  <Chip 
                    label={incompleteCount} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Informed</span>
                {raciTab === 3 && incompleteCount > 0 && (
                  <Chip 
                    label={incompleteCount} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      minWidth: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'warning.main',
                      color: 'white',
                      fontWeight: 600
                    }} 
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Mobile Toolbar */}
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {/* Search Bar - Full width on mobile */}
          {mobileSearchOpen ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} style={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); }}>
                <X size={20} />
              </IconButton>
            </Box>
          ) : null}
          
          {/* Quick Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search Toggle */}
            <IconButton
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              sx={{ 
                border: '1px solid',
                borderColor: mobileSearchOpen ? 'primary.main' : 'divider',
                borderRadius: 2,
                color: mobileSearchOpen ? 'primary.main' : 'text.secondary',
              }}
            >
              <Search size={20} />
            </IconButton>

            {/* Filter Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<Filter size={16} />}
              onClick={() => setMobileFilterOpen(true)}
              sx={{ 
                borderColor: 'divider',
                minWidth: 'auto',
                px: 1.5,
              }}
            >
              {activeFilterCount > 0 && (
                <Chip
                  size="small"
                  label={activeFilterCount}
                  color="primary"
                  sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                />
              )}
            </Button>

            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{ ml: 'auto' }}
            >
              <ToggleButton value="list" sx={{ px: 1.5 }}>
                <List size={18} />
              </ToggleButton>
              <ToggleButton value="kanban" sx={{ px: 1.5 }}>
                <LayoutGrid size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Mobile Filter Drawer */}
          <SwipeableDrawer
            anchor="bottom"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            onOpen={() => setMobileFilterOpen(true)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: '80vh',
                pb: 'env(safe-area-inset-bottom)',
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              {/* Handle bar */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box sx={{ width: 40, height: 4, bgcolor: 'grey.300', borderRadius: 2 }} />
              </Box>
              
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Filters
              </Typography>

              {/* Status Filters */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                STATUS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {Object.values(TaskStatus).map((status) => (
                  <Chip
                    key={status}
                    label={status.replace('_', ' ')}
                    onClick={() => handleStatusFilter(status)}
                    color={filters.status?.includes(status) ? 'primary' : 'default'}
                    variant={filters.status?.includes(status) ? 'filled' : 'outlined'}
                    sx={{ textTransform: 'capitalize' }}
                  />
                ))}
              </Box>

              {/* Priority Filters */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                PRIORITY
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {Object.values(TaskPriority).map((priority) => (
                  <Chip
                    key={priority}
                    label={priority.toUpperCase()}
                    onClick={() => handlePriorityFilter(priority)}
                    color={filters.priority?.includes(priority) ? 'primary' : 'default'}
                    variant={filters.priority?.includes(priority) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => { handleClearFilters(); setMobileFilterOpen(false); }}
                  disabled={activeFilterCount === 0}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setMobileFilterOpen(false)}
                >
                  Apply Filters
                </Button>
              </Box>
            </Box>
          </SwipeableDrawer>
        </Box>
      ) : (
        /* Desktop Toolbar */
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} style={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          {/* Filters */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Filter size={16} />}
            endIcon={
              activeFilterCount > 0 ? (
                <Chip
                  size="small"
                  label={activeFilterCount}
                  color="primary"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              ) : (
                <ChevronDown size={14} />
              )
            }
            onClick={handleFilterClick}
            sx={{ borderColor: 'divider' }}
          >
            Filters
          </Button>

          {/* Filter Menu */}
          <Menu
            anchorEl={filterAnchor}
            open={Boolean(filterAnchor)}
            onClose={handleFilterClose}
            PaperProps={{ sx: { minWidth: 240, p: 1 } }}
          >
            <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              STATUS
            </Typography>
            {Object.values(TaskStatus).map((status) => (
              <MenuItem
                key={status}
                onClick={() => handleStatusFilter(status)}
                selected={filters.status?.includes(status)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                {status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </MenuItem>
            ))}
            
            <Divider sx={{ my: 1 }} />
            
            <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              PRIORITY
            </Typography>
            {Object.values(TaskPriority).map((priority) => (
              <MenuItem
                key={priority}
                onClick={() => handlePriorityFilter(priority)}
                selected={filters.priority?.includes(priority)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                {priority.toUpperCase()}
              </MenuItem>
            ))}

            {activeFilterCount > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={handleClearFilters} sx={{ color: 'error.main' }}>
                  Clear All Filters
                </MenuItem>
              </>
            )}
          </Menu>

          <Box sx={{ flex: 1 }} />

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="list">
              <List size={18} />
            </ToggleButton>
            <ToggleButton value="kanban">
              <LayoutGrid size={18} />
            </ToggleButton>
            <ToggleButton value="calendar">
              <Calendar size={18} />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Create Task Button */}
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleCreateTask}
          >
            New Task
          </Button>
        </Box>
      )}

      {/* Task Views */}
      {viewMode === 'list' && (
        <Box>
          <TaskListView tasks={filteredTasks} isLoading={isLoading} currentRaciRole={selectedRaciRole} />
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}
      {viewMode === 'kanban' && (
        <Box>
          <TaskKanbanView tasks={filteredTasks} isLoading={isLoading} />
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}
      {viewMode === 'calendar' && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Calendar view coming soon
          </Typography>
        </Box>
      )}

      {/* Mobile FAB for Create Task */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={handleCreateTask}
          sx={{
            position: 'fixed',
            bottom: 80, // Above bottom nav
            right: 16,
            zIndex: 1100,
          }}
        >
          <Plus size={24} />
        </Fab>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={!!selectedTaskId}
        taskId={selectedTaskId}
        onClose={() => setSearchParams({})}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </Box>
  );
};

export default TasksPage;
